'use client'

import { useEffect, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'

interface OnboardingSpotlightProps {
  target?: string
  padding?: number
  isVisible: boolean
  allowInteraction?: boolean
  disableOverlay?: boolean
  children?: React.ReactNode
}

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

export function OnboardingSpotlight({
  target,
  padding = 8,
  isVisible,
  allowInteraction = false,
  disableOverlay = false,
  children,
}: OnboardingSpotlightProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)

  const updateTargetRect = useCallback(() => {
    if (!target) {
      setTargetRect(null)
      return
    }

    const element = document.querySelector(target)
    if (!element) {
      setTargetRect(null)
      return
    }

    const rect = element.getBoundingClientRect()
    setTargetRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    })
  }, [target, padding])

  useEffect(() => {
    if (!isVisible) return

    updateTargetRect()

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updateTargetRect)
    }

    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    // Also observe DOM changes
    const observer = new MutationObserver(handleUpdate)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    })

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      observer.disconnect()
    }
  }, [isVisible, updateTargetRect])

  if (!isVisible) return null

  // Full screen overlay with cutout for target
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      {/* Semi-transparent overlay with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: allowInteraction ? 'none' : 'auto' }}
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White = visible, Black = hidden */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        {!disableOverlay && (
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
          />
        )}
      </svg>

      {/* Spotlight border animation */}
      {targetRect && (
        <div
          className={cn(
            "absolute rounded-lg transition-all duration-300 ease-out",
            "ring-2 ring-primary ring-offset-2 ring-offset-transparent",
            "animate-pulse"
          )}
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            pointerEvents: allowInteraction ? 'none' : 'auto',
          }}
        />
      )}

      {children}
    </div>
  )
}
